export function throttle(func, waitTime) {
	let timeout = null
	let previous = 0

	const later = () => {
		previous = Date.now()
		timeout = null
		func()
	}

	return function() {
		const now = Date.now()
		const remaining = waitTime - (now - previous)
		if (remaining <= 0 || remaining > waitTime) {
			if (timeout) clearTimeout(timeout)
			later()
		} else if (!timeout) {
			timeout = setTimeout(later, remaining)
		}
	}
}

export function debounce(func, delay) {
	let inDebounce
	return function() {
		const context = this
		const args = arguments
		clearTimeout(inDebounce)
		inDebounce = setTimeout(() => func.apply(context, args), delay)
	}
}

export function promisifiedDebounce(func, delay) {
	let inDebounce
	return function () {
		const context = this
		const args = arguments
		clearTimeout(inDebounce)
		return new Promise((resolve) => {
			inDebounce = setTimeout(() => resolve(func.apply(context, args)), delay)
		})
	}
}

/**
 * Remove inline styles from an element
 * @param {HTMLElement} element Element to remove the styles from
 * @param  {...string} props    Styles to remove
 */
export function removeStyle(element, ...props) {
	props.forEach(prop => {
		element.style[prop] = ''
		if (element.getAttribute('style') === '') {
			element.removeAttribute('style')
		}
	})
}

/**
 * Local reference to the last saved scroll position
 * @prop {number} x Horizontal scroll position
 * @prop {number} y Vertical scroll position
 */
const scrollPos = {
	x: window.pageXOffset,
	y: window.pageYOffset
}

/**
 * Disables scrolling on the whole page
 */
export function disableBodyScrolling() {
	if (isMobileSafari) {
		scrollPos.x = window.pageXOffset
		scrollPos.y = window.pageYOffset

		Object.assign(document.documentElement.style, {
			top:      `-${scrollPos.y}px`,
			left:     `-${scrollPos.x}px`,
			position: 'fixed',
			width:    '100vw',
			height:   '100vh',
			overflow: 'hidden'
		})
	} else {
		document.documentElement.style.overflow = 'hidden'
	}
}

/**
 * Enables scrolling again and restores scroll position
 */
export function enableBodyScrolling() {
	if (isMobileSafari) {
		removeStyle(document.documentElement, ...[
			'top',
			'left',
			'position',
			'width',
			'height',
			'overflow'
		])
		window.scrollTo(scrollPos.x, scrollPos.y)
	} else {
		removeStyle(document.documentElement, 'overflow')
	}
}


/**
 * Checks if the user agent identifies itself as mobile Safari
 * @returns {boolean}
 */
export const isMobileSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;


/**
 * Sets the height in a way that allows CSS height transition to work
 * @param {HTMLElement}   element The element that should receive the height
 * @param {string|number} height  The height to set on the element
 */
export function setTransitionableHeight(element, height) {
	element.style.height = 'auto'
	element.style.transition = 'none'

	element.style.height = element.offsetHeight + 'px'
	element.style.transition = ''

	setTimeout(() => {
		element.style.height = isNaN ? height + 'px' : height
	}, 1)
}


/**
 * Unsets the height in a way that allows CSS height transition to work
 * @param {HTMLElement} element The element on wich the height should be removed
 */
export function unsetTransitionableHeight(element) {
	const initialHeight = element.style.height

	element.style.height = 'auto'
	element.style.transition = 'none'

	const height = element.offsetHeight

	element.style.height = initialHeight

	setTimeout(() => {
		element.style.transition = ''
		element.style.height = height + 'px'

		// Clean up after transition end
		element.addEventListener('transitionend', () => {
			removeStyle(element, 'height')
		}, { once: true })
	}, 1)
}

/**
 *
 * @param {HTMLElement} element
 */
export function removeChildren(element) {
	while (element.firstChild) {
		element.removeChild(element.firstChild)
	}
}

/**
 * Wait for an event to finish and optionally run a check on it to see if it's done
 * @param {Element}                  element Element to apply the event listener to
 * @param {string|[string,Function]} event   Either the event name or an array of event name and function that, given the event object checks if the event is done
 */
export const awaitEvent = (element, event) => new Promise(resolve => {
	let eventName
	let eventCheck

	// Decompose event into event name and event check if it is an array
	if (Array.isArray(event)) {
		[eventName, eventCheck] = event
	} else {
		eventName = event
	}

	// Resolve immediately if there was no event name given
	if (!eventName) return resolve()

	const eventHandler = e => {
		// Don`t resolve if an event check was given and it returns false
		if (eventCheck && !eventCheck(e)) return

		// At this point either the check passed or was not given,
		// remove event listener and resolve promise
		element.removeEventListener(eventName, eventHandler)
		resolve()
	}

	element.addEventListener(eventName, eventHandler)
})

export function getEventPath(e) {
	function polyfill() {
		let element = e.target || null;
		const pathArr = [element];

		if (!element || !element.parentElement) {
			return [];
		}

		while (element.parentElement) {
			element = element.parentElement;
			pathArr.unshift(element);
		}

		return pathArr;
	}

	return e.path || (e.composedPath && e.composedPath()) || polyfill();
}

/**
 * Check for iterability
 * @param  {*} obj
 */
export function isIterable(obj) {
	return obj == null ? false : typeof obj[Symbol.iterator] === 'function'
}

/**
 * Convert anything to an array
 * @param  {*} obj
 */
export function toArray(obj) {
	return obj == null ? [] : isIterable(obj) ? [...obj] : [obj]
}

/**
 * Return the base name of a file path
 * @param {string} str File path
 */
export function baseName(str) {
	const base = new String(str).substring(str.lastIndexOf('/') + 1)
	if (base.lastIndexOf('.') != -1) {
		return base.substring(0, base.lastIndexOf('.'))
	}
	return base
}

/**
 * Get the index of an element in its parent
 * @param {HTMLElement} element
 */
export function getElementIndex(element) {
  return [...element.parentNode.children].indexOf(element)
}
